import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, AlertCircle, ShieldCheck, Radar } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../api/auth';
import { AwareNowLogo } from '../components/brand/AwareNowLogo';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import '../components/layout/shell.css';

export function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authApi.login({ username, password });
      const { user, token } = response.data.data;
      setAuth(user, token);
      navigate(user.password_change_required ? '/settings' : '/');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <header className="login-header"><AwareNowLogo /><ThemeToggle /></header>
      <main className="login-main">
        <section className="login-story">
          <div className="login-kicker"><span /> AwareNow workspace</div>
          <h1>Make awareness<br />part of every<br />working day.</h1>
          <p>Run thoughtful simulations, investigate reported messages, and see where your people need support.</p>
          <div className="login-capabilities"><span><Radar size={18} /> Simulate threats</span><span><ShieldCheck size={18} /> Build resilience</span><span><ShieldCheck size={18} /> Understand exposure</span></div>
        </section>
        <section className="login-card" aria-labelledby="login-heading">
          <span className="login-card-icon"><ShieldCheck size={25} /></span>
          <p className="login-kicker">Workspace access</p>
          <h2 id="login-heading">Sign in to AwareNow</h2>
          <p className="login-description">Sign in to your AwareNow account.</p>
          {error && <div role="alert" className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg mb-4"><AlertCircle className="w-5 h-5 flex-shrink-0" /><span className="text-sm">{error}</span></div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div><label htmlFor="username" className="label">Username</label><input id="username" type="text" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} className="input" placeholder="Enter your username" required autoFocus /></div>
            <div><label htmlFor="password" className="label">Password</label><input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="input" placeholder="Enter your password" required /></div>
            <Button type="submit" className="w-full login-submit" loading={loading}>Sign In <ArrowRight size={17} /></Button>
          </form>
          <p className="login-access"><ShieldCheck size={14} /> Authorized access only</p>
        </section>
      </main>
      <footer className="login-footer"><span>© {new Date().getFullYear()} AwareNow</span><span>Awareness operations workspace</span></footer>
    </div>
  );
}
